// The "Suitability Entity Fact Find - Existing Entity Clients" v2 form (SEP-351).
//
// An entity is a company or another corporate body: a company, an LLP or a
// partnership, a trust, a registered charity, a pension scheme (a SIPP or an
// SSAS), or another legal structure. A person who is authorised to act for the
// entity completes the form: a director, a trustee, a partner, a board member
// or another authorised individual. The answers are about the entity: its
// objectives, assets, liabilities, income, expenditure, account usage and
// risk tolerance.
//
// The firm uses this ONE form for each fact find of an entity that the
// platform opens from SEP-351: the suitability refresh of an existing entity
// client, and the onboarding of an entity prospect. A version that uses it
// holds `formLayout: 'entity_suitability'`. An entity version made before
// SEP-351 keeps the entity form of `entity-fact-find.pdf`.
//
// # One definition for the web and for the server
//
// The web form and the server both read the sections below. The web gives
// them to its form renderer as they are. The server turns them into the
// fields of its readers (`fact-find-form-fields-entity-suitability.ts`). There
// is no second copy, so the two cannot drift.
//
// # The words
//
// Each label, option, introduction and note is the text of the firm form,
// without the colon at the end of a label. These are not in the form:
//
//   - the `why` of a question. It is empty, except where the form prints a
//     note under the question;
//   - the label of a cell of a repeated row, for example "Change 1 - name".
//     The form prints a table with a column for each part;
//   - the computed totals note, which the other forms also use.
//
// # Question ids
//
// A question that an earlier fact find of an entity also asks keeps its id,
// so the suggestion boxes of a refresh and the change summary match it. A
// question with new options or a new type has a new id: `entity_legal_form`
// replaces `entity_type`, `entity_investment_decisions` is a multiple choice,
// and `entity_account_usage` has the entity options.
//
// # The two audiences
//
// The form is for an existing entity client. An entity prospect reads it with
// the onboarding audience (Dowgate decision of 2026-10-21): the form adds the
// "Source of Wealth & Funds" section of the onboarding form of an entity, it
// removes the questions about a change since the previous review, and it
// reads the `onboarding*` words beside the words of the form. `audience` tags
// each such item (`fact-find-audience.ts`).
//
// # The risk questions
//
// The form asks the twelve scored risk questions in entity words. The stored
// answer stays the canonical option label of `atr-scoring.ts`, so the score
// and the band do not change. Each entity option takes the canonical option at
// the same position, as the "Existing Clients" form does (SEP-288).

import type { AtrQuestionWording } from './fact-find-existing-client';
import {
    EXISTING_CLIENT_ASSET_CLASSES,
    existingClientAssetScope,
} from './fact-find-existing-client';
import { REGULAR_WITHDRAWAL_FREQUENCIES } from './fact-find-withdrawal-frequency';
import type {
    AudienceQuestionKeys,
    AudienceSectionKeys,
    AudienceTableKeys,
} from './fact-find-audience';
import {
    SOURCE_OF_WEALTH_DESCRIPTION,
    SOURCE_OF_WEALTH_INTRO,
    SOURCE_OF_WEALTH_QUESTIONS,
    SOURCE_OF_WEALTH_SECTION_ID,
    SOURCE_OF_WEALTH_TITLE,
} from './fact-find-source-of-wealth';

/** The value kinds that the entity form asks. A subset of the web `QuestionType`. */
export type EntitySuitabilityQuestionType =
    | 'short_text'
    | 'long_text'
    | 'currency'
    | 'date'
    | 'single_select'
    | 'multi_select'
    | 'yes_no'
    | 'country_select';

/** One question of the entity form. The shape is a subset of the web `FactFindQuestion`. */
export interface EntitySuitabilityQuestion extends AudienceQuestionKeys {
    readonly id: string;
    readonly type: EntitySuitabilityQuestionType;
    readonly label: string;
    /** The note under the question. Empty when the form prints none. */
    readonly why: string;
    readonly options?: string[];
    readonly optionNotes?: Readonly<Record<string, string>>;
    readonly placeholder?: string;
    readonly dependsOn?: { questionId: string; equals: string[] };
    readonly computed?: true;
    readonly control?: 'dropdown';
    readonly addressLookup?: {
        line1: string;
        line2: string;
        city: string;
        postCode: string;
    };
}

/** The start of one table of the form. The shape is a subset of the web `FactFindFormTable`. */
export interface EntitySuitabilityTable extends AudienceTableKeys {
    readonly firstQuestionId: string;
    readonly heading: string;
    readonly columns?: readonly string[];
    /** The paragraph that the form prints above the table. */
    readonly intro?: string;
    /** The third column holds the "Details or notes" box of each money row. */
    readonly beside?: 'detail';
}

/** One section of the entity form. The shape is a subset of the web `FactFindSection`. */
export interface EntitySuitabilitySection extends AudienceSectionKeys {
    readonly id: string;
    readonly title: string;
    readonly description: string;
    readonly intro?: string;
    /** The note that the form prints under the section. */
    readonly outro?: string;
    readonly tables?: readonly EntitySuitabilityTable[];
    /** `atr` marks the scored risk questions, which each side builds from its instrument. */
    readonly kind: 'questions' | 'atr';
    readonly questions: EntitySuitabilityQuestion[];
}

/** The entity types of the form, in its order. */
export const ENTITY_LEGAL_FORMS = [
    'Company / corporate',
    'LLP / partnership',
    'Trust',
    'Registered charity',
    'Pension scheme',
    'Other',
] as const;

/** The entity types that need the legal form or the subtype in words. */
export const ENTITY_LEGAL_FORMS_WITH_DETAIL = ['Trust', 'Pension scheme', 'Other'] as const;

/** The investment objectives of the form, in its order. */
export const ENTITY_INVESTMENT_OBJECTIVES = [
    'Income',
    'Growth',
    'Balance of Income and Growth',
    'Capital Preservation',
] as const;

/** The note under each objective, word for word from the form. */
export const ENTITY_INVESTMENT_OBJECTIVE_EXPLANATIONS: Readonly<
    Record<(typeof ENTITY_INVESTMENT_OBJECTIVES)[number], string>
> = {
    Income:
        'The priority is regular payments from the portfolio, for example to supplement income. ' +
        'Income-focused portfolios often hold more dividend-paying shares and bonds, but their value will still rise and fall.',
    Growth:
        "The priority is increasing the portfolio's value over time rather than receiving regular income. " +
        'Growth portfolios may experience more short-term movement in pursuit of long-term gains.',
    'Balance of Income and Growth':
        'The entity wants a mix of regular income and long-term growth. This provides a middle path between income and growth.',
    'Capital Preservation':
        "The priority is protecting the portfolio's value and limiting losses rather than seeking higher returns. " +
        'Its value may still rise and fall, and no investment can guarantee against loss.',
};

/** How the investment decisions of the entity have been made, in the order of the form. */
export const ENTITY_INVESTMENT_DECISIONS = [
    'By authorised individual(s)',
    'By the board, trustees, partners or investment committee',
    'With financial advice',
    'By a discretionary investment manager',
    'A combination of these',
] as const;

/** The "Other" option of the account usage question. */
export const ENTITY_ACCOUNT_USAGE_OTHER = 'Other - please provide details below';

/** How the entity uses its Dowgate account, in the order of the form. */
export const ENTITY_ACCOUNT_USAGES = [
    'To hold and invest a lump sum or entity reserves',
    'To hold investments transferred from elsewhere or make further transfers',
    'To invest regular contributions or surplus cash',
    'To receive investment income',
    'To hold cash pending investment',
    'To invest proceeds from the sale of property, a business, investments or other assets',
    'To meet operating costs, grants, beneficiary distributions or pension payments',
    'To invest in a particular company, fund, offer or corporate action',
    ENTITY_ACCOUNT_USAGE_OTHER,
] as const;

const EXPERIENCE_LENGTHS = [
    'No experience',
    'Under 1 year',
    '1-3 years',
    '4-6 years',
    '7-9 years',
    '10+ years',
];

const AGREEMENT_OPTIONS = [
    'Strongly disagree',
    'Disagree',
    'Somewhat disagree',
    'Somewhat agree',
    'Agree',
    'Strongly agree',
];

/** The money columns of the two financial tables of the form. */
export const ENTITY_SUITABILITY_MONEY_COLUMNS = ['Description', 'Figure', 'Details or notes'];

/**
 * The entity words of the risk questions, by question id. Each option maps the
 * canonical label of `atr-scoring.ts` to the words of the form, by position.
 */
const ATR_WORDING_PAIRS: readonly (readonly [string, AtrQuestionWording])[] = [
    [
        'atr_q1',
        {
            prompt: "Preserving the entity's capital is more important than the opportunity to achieve higher returns.",
        },
    ],
    [
        'atr_q2',
        {
            prompt: 'The entity would prefer fewer ups and downs in the value of its portfolio, even if this meant accepting lower potential returns.',
        },
    ],
    [
        'atr_q3',
        {
            prompt:
                'Smaller companies can grow faster than larger, well-established companies. However, their shares can be harder to sell ' +
                'and are at greater risk of failure. The entity would be comfortable investing in smaller companies for the potential to achieve a higher return.',
        },
    ],
    [
        'atr_q4',
        {
            prompt:
                'Shares in smaller companies can rise and fall in value more sharply than shares in larger companies. ' +
                'The entity would be comfortable remaining invested through these fluctuations.',
        },
    ],
    [
        'atr_q5',
        {
            prompt: "If the entity's Dowgate portfolio fell by 25% over four weeks, what would the authorised decision-maker be most likely to do?",
        },
    ],
    [
        'atr_q6',
        {
            prompt:
                "If the entity's portfolio fell in value and remained below the amount originally invested for several years, " +
                'how comfortable would the entity be remaining invested?',
            options: {
                'Any fall would be cause for concern': 'Very uncomfortable',
                'Loss of 15%': 'Uncomfortable',
                'Loss of 16-30%': 'Neither Comfortable nor Uncomfortable',
                'Loss of 31-49%': 'Comfortable',
                'Loss of 50%+': 'Very comfortable',
            },
        },
    ],
    [
        'atr_q7',
        {
            prompt: 'When does the entity expect to need to withdraw most or all of the money from its portfolio?',
            options: {
                '0-1 years': 'Within 1 year',
                '1-3 years': 'In 1-3 years',
                '3-5 years': 'In 4-5 years',
                '5-7 years': 'In 6-7 years',
                '7+ years': '8 years or more',
            },
        },
    ],
    [
        'atr_q8',
        {
            prompt: "How secure and predictable are the entity's current sources of income or funding?",
            options: { 'Very unsecure': 'Very insecure', Unsecure: 'Insecure' },
        },
    ],
    [
        'atr_q9',
        {
            prompt: 'Over the relevant investment horizon, how does the entity expect its income or funding to change?',
            options: { 'Remain stable': 'Remain broadly the same' },
        },
    ],
    [
        'atr_q10',
        {
            prompt: 'Over the relevant investment horizon, how does the entity expect its liabilities and other financial commitments to change?',
            options: { 'Remain stable': 'Remain broadly the same' },
        },
    ],
    [
        'atr_q11',
        {
            prompt: "Approximately how many months of essential expenditure or commitments could the entity's readily accessible reserves cover?",
            options: { 'No emergency funds': 'No reserves', '6-18 months': '7-18 months' },
        },
    ],
    [
        'atr_q12',
        {
            prompt:
                "How much of the entity's investments with Dowgate could be lost without materially affecting its objectives, " +
                'operations, liabilities or obligations to beneficiaries, members or pension holders?',
            options: {
                'No losses (0-10%)': 'Minimal losses (0-10%)',
                'Moderate losses (21-49%)': 'Moderate losses (21-40%)',
                'Large losses (50-90%)': 'Large losses (41-69%)',
                'Total losses (91-100%)': 'Maximum losses (70-100%)',
            },
        },
    ],
];

export const ENTITY_SUITABILITY_ATR_WORDING: Readonly<Record<string, AtrQuestionWording>> =
    Object.fromEntries(ATR_WORDING_PAIRS);

/** The introduction of each section, word for word from the form. */
const INTROS = {
    entityInformation:
        "As part of our regulatory obligations, we need to maintain accurate and up-to-date information about the entity, its ownership and control, and the people authorised to act for it. This helps us assess whether the existing service and investment strategy remain suitable and ensures our records reflect the entity's current circumstances.",
    knowledge:
        'The investment objectives and financial circumstances in this form relate to the entity. The knowledge and experience questions should be answered by, or reflect, the person authorised to make investment decisions or give instructions on the entity’s behalf.',
    financial:
        "As part of our suitability assessment, we need to understand the entity's current financial position. Please provide the entity's assets, liabilities, income, expenditure and material commitments. Dowgate portfolio values will be added from our records when we complete the internal assessment.",
    liquidity:
        'It is important that we understand how the entity currently uses, and expects to use, its Dowgate account, including planned contributions and withdrawals. This supports both the suitability assessment and our understanding of expected account activity.',
    risk: "Understanding the entity's attitude to risk and capacity for loss helps us assess whether its existing investment strategy remains suitable. Please answer on behalf of the entity, taking account of its objectives, governing documents, liabilities and ability to meet ongoing obligations.",
    declaration:
        'Please confirm that the lead person signing this form is authorised to act for the entity and that any approvals required under its governing documents have been obtained.',
    declarationStatement:
        "By signing, the lead authorised person confirms that they are authorised to act for the entity and that the information provided is accurate and up to date to the best of their knowledge. They understand that Dowgate will use this information to assess whether the entity's existing service and investment strategy remain suitable. The entity lead will notify its investment manager promptly of any material change to its circumstances, objectives, financial position, ownership, control or authority.",
} as const;

/**
 * The introduction paragraphs for an entity prospect (Dowgate decision of
 * 2026-10-21). The firm approved the words. They say the same thing as the
 * words of the form, without the words for an existing client.
 */
const ONBOARDING_INTROS = {
    entityInformation:
        "As part of our regulatory obligations, we need accurate and up-to-date information about the entity, its ownership and control, and the people authorised to act for it. This helps us assess whether our service and investment strategy are suitable for the entity and ensures our records reflect the entity's circumstances.",
    financial:
        "As part of our suitability assessment, we need to understand the entity's current financial position. Please provide the entity's assets, liabilities, income, expenditure and material commitments.",
    liquidity:
        'It is important that we understand how the entity expects to use its Dowgate account, including planned contributions and withdrawals. This supports both the suitability assessment and our understanding of expected account activity.',
    risk: "Understanding the entity's attitude to risk and capacity for loss helps us assess whether an investment strategy is suitable for it. Please answer on behalf of the entity, taking account of its objectives, governing documents, liabilities and ability to meet ongoing obligations.",
    declarationStatement:
        'By signing, the lead authorised person confirms that they are authorised to act for the entity and that the information provided is accurate and complete to the best of their knowledge. They understand that Dowgate will use this information to assess whether our service and investment strategy are suitable for the entity. The entity lead will notify its investment manager promptly of any material change to its circumstances, objectives, financial position, ownership, control or authority.',
} as const;

/** A question about a change since the previous review. An entity prospect has no previous review. */
function forRefresh(question: EntitySuitabilityQuestion): EntitySuitabilityQuestion {
    return { ...question, audience: 'refresh' };
}

const YES = ['Yes'];

function q(
    id: string,
    type: EntitySuitabilityQuestionType,
    label: string,
    extra: Partial<Omit<EntitySuitabilityQuestion, 'id' | 'type' | 'label'>> = {},
): EntitySuitabilityQuestion {
    return { id, type, label, why: '', ...extra };
}

/** One money row: the figure, and its "Details or notes" box. */
function moneyRow(id: string, label: string): EntitySuitabilityQuestion[] {
    return [
        q(id, 'currency', label),
        q(`${id}_detail`, 'long_text', `${label} — details or notes`),
    ];
}

/** A computed total. The value comes from `fact-find-totals.ts` and is never stored. */
function total(id: string, label: string, rows: string): EntitySuitabilityQuestion {
    return q(id, 'currency', label, {
        why: `We add up the ${rows} you listed above. There is nothing to type here.`,
        computed: true,
    });
}

/** The rows of the table of changes to the people of the entity. The form prints two. */
function personChangeRows(): EntitySuitabilityQuestion[] {
    const when = { questionId: 'changes_to_officers', equals: YES };
    return [1, 2]
        .flatMap((n): EntitySuitabilityQuestion[] => [
            q(`person_change_${n}_name`, 'short_text', `Change ${n} — name`, { dependsOn: when }),
            q(`person_change_${n}_role`, 'short_text', `Change ${n} — role or capacity`, {
                dependsOn: when,
            }),
            q(`person_change_${n}_nature`, 'short_text', `Change ${n} — nature of change`, {
                dependsOn: when,
            }),
            q(`person_change_${n}_date`, 'date', `Change ${n} — effective date`, {
                dependsOn: when,
            }),
        ])
        .map(forRefresh);
}

/** The asset class grid. Each class asks two questions, with the scope of the class as the note. */
function assetGrid(): EntitySuitabilityQuestion[] {
    return EXISTING_CLIENT_ASSET_CLASSES.flatMap((assetClass) => [
        q(
            `asset_invested_${assetClass.key}`,
            'yes_no',
            `${assetClass.name} — have they invested in this themselves?`,
            { why: existingClientAssetScope(assetClass) },
        ),
        q(
            `asset_explain_${assetClass.key}`,
            'yes_no',
            `${assetClass.name} — could they explain how it works and its main risks?`,
            { why: existingClientAssetScope(assetClass) },
        ),
    ]);
}

/**
 * The asset rows of the entity form, for the computed totals. Each list is in
 * the order of the form.
 */
export const ENTITY_SUITABILITY_TOTAL_ASSETS_COMPONENT_IDS: readonly string[] = [
    'entity_cash_reserves',
    'property_holdings_value',
    'investments_outside_dowgate',
    'business_assets_value',
    'other_assets_value',
];
export const ENTITY_SUITABILITY_TOTAL_LIABILITIES_COMPONENT_IDS: readonly string[] = [
    'borrowings_outstanding',
    'other_liabilities',
    'contingent_liabilities',
];
export const ENTITY_SUITABILITY_TOTAL_INCOME_COMPONENT_IDS: readonly string[] = [
    'trading_income',
    'investment_income',
    'gifts_donations_grants',
    'other_income',
];
export const ENTITY_SUITABILITY_TOTAL_EXPENDITURE_COMPONENT_IDS: readonly string[] = [
    'administrative_expenses',
    'distributions',
    'debt_servicing',
    'other_commitments',
];

/** The sections of the form, in its order. */
export const ENTITY_SUITABILITY_SECTIONS: readonly EntitySuitabilitySection[] = [
    {
        id: 'entity_information',
        title: 'Entity Information Confirmation',
        onboardingTitle: 'Entity Information',
        description: 'The entity, its address, its registration and the people who control it.',
        intro: INTROS.entityInformation,
        onboardingIntro: ONBOARDING_INTROS.entityInformation,
        outro: 'For any new person, we may request identity, verification and authority information separately.',
        tables: [
            { firstQuestionId: 'reg_address_line1', heading: 'Registered address' },
            { firstQuestionId: 'registration_number', heading: 'Entity details' },
        ],
        kind: 'questions',
        questions: [
            q('entity_name', 'short_text', 'Legal name'),
            q('trading_name', 'short_text', 'Trading name, if different'),
            q('primary_contact', 'short_text', 'Primary contact name and role'),
            q('primary_contact_details', 'short_text', 'Telephone number and email address'),
            q('entity_legal_form', 'single_select', 'Entity type or legal form', {
                options: [...ENTITY_LEGAL_FORMS],
            }),
            q(
                'entity_legal_form_detail',
                'short_text',
                'If Trust, Pension Scheme or Other, please specify the legal form or subtype',
                {
                    dependsOn: {
                        questionId: 'entity_legal_form',
                        equals: [...ENTITY_LEGAL_FORMS_WITH_DETAIL],
                    },
                },
            ),
            q('reg_address_line1', 'short_text', 'Address line 1'),
            q('reg_address_line2', 'short_text', 'Address line 2'),
            q('reg_city', 'short_text', 'City'),
            q('reg_post_code', 'short_text', 'Post code', {
                addressLookup: {
                    line1: 'reg_address_line1',
                    line2: 'reg_address_line2',
                    city: 'reg_city',
                    postCode: 'reg_post_code',
                },
            }),
            q('reg_country', 'country_select', 'Country'),
            q(
                'principal_place_of_business',
                'short_text',
                'Principal place of business, if different',
            ),
            q('registration_number', 'short_text', 'Registration number'),
            q('hmrc_number', 'short_text', 'Charity / HMRC reference'),
            q('lei_number', 'short_text', 'LEI number'),
            q('registered_jurisdiction', 'short_text', 'Registered jurisdiction'),
            q('tax_residences', 'short_text', 'Tax residence(s)'),
            q('website', 'short_text', 'Website, if applicable'),
            q(
                'entity_purpose',
                'long_text',
                "Briefly describe the entity's current nature, purpose and principal activities",
            ),
            forRefresh(
                q(
                    'entity_changes',
                    'yes_no',
                    "Since the previous review, have there been any changes to the entity's legal name or form, registered office, jurisdiction, tax residence, ownership or control, principal activities, or any governing or authority document (including its trust deed, articles, scheme rules, investment policy, or board or trustee resolutions)?",
                ),
            ),
            forRefresh(
                q(
                    'entity_changes_detail',
                    'long_text',
                    'If Yes, please provide details, the effective date and an updated copy where applicable',
                    { dependsOn: { questionId: 'entity_changes', equals: YES } },
                ),
            ),
            forRefresh(
                q(
                    'changes_to_officers',
                    'yes_no',
                    'Have there been any changes to directors, partners, members, settlors, trustees, protectors, beneficiaries, beneficial owners, controllers or authorised signatories?',
                ),
            ),
            ...personChangeRows(),
        ],
    },
    {
        id: 'knowledge',
        title: 'Investment Objectives and Knowledge and Experience',
        description: 'The objective of the entity, and the experience of its decision-maker.',
        intro: INTROS.knowledge,
        tables: [
            {
                firstQuestionId: 'asset_invested_cash_deposits',
                heading:
                    'What knowledge and experience does the relevant investment decision-maker have of the following asset classes?',
            },
        ],
        kind: 'questions',
        questions: [
            q(
                'knowledge_decision_maker',
                'short_text',
                'Name and role of the person whose knowledge and experience are reflected below',
            ),
            q(
                'investment_objective',
                'single_select',
                "What best describes the entity's current investment objective?",
                {
                    options: [...ENTITY_INVESTMENT_OBJECTIVES],
                    optionNotes: ENTITY_INVESTMENT_OBJECTIVE_EXPLANATIONS,
                },
            ),
            q(
                'experience_length',
                'single_select',
                'How long has the relevant investment decision-maker been investing, excluding cash deposits?',
                { options: EXPERIENCE_LENGTHS },
            ),
            q(
                'knowledgeable_investor',
                'single_select',
                'The relevant investment decision-maker understands how investments work and the risks involved',
                { options: AGREEMENT_OPTIONS },
            ),
            q(
                'worked_in_finance',
                'yes_no',
                'Has the relevant decision-maker worked in a financial services role involving investments?',
            ),
            q(
                'worked_in_finance_detail',
                'long_text',
                'If Yes, provide details of the role and relevant experience',
                { dependsOn: { questionId: 'worked_in_finance', equals: YES } },
            ),
            q(
                'financial_qualifications',
                'yes_no',
                'Does the relevant decision-maker hold, or have they previously held, any relevant financial or investment qualifications?',
            ),
            q('financial_qualification_detail', 'short_text', 'If Yes, provide details', {
                why: 'For example CFA, IMC or relevant CISI or CII qualifications.',
                dependsOn: { questionId: 'financial_qualifications', equals: YES },
            }),
            ...assetGrid(),
            q(
                'other_products_experience',
                'long_text',
                'Please provide details of any other investments or financial products in which the relevant decision-maker has experience of, including approximately how long you have invested in them',
            ),
            q(
                'entity_investment_decisions',
                'multi_select',
                'How have investment decisions for the entity generally been made? Please select all that apply',
                { options: [...ENTITY_INVESTMENT_DECISIONS] },
            ),
        ],
    },
    {
        id: 'financial_situation',
        title: 'Financial Circumstances',
        description: 'The assets, liabilities, income and expenditure of the entity.',
        intro: INTROS.financial,
        onboardingIntro: ONBOARDING_INTROS.financial,
        tables: [
            {
                firstQuestionId: 'entity_cash_reserves',
                heading: 'Assets and Liabilities',
                columns: ENTITY_SUITABILITY_MONEY_COLUMNS,
                beside: 'detail',
            },
            {
                firstQuestionId: 'trading_income',
                heading: 'Annual Gross Income and Expenditure',
                columns: ENTITY_SUITABILITY_MONEY_COLUMNS,
                beside: 'detail',
            },
        ],
        kind: 'questions',
        questions: [
            q('financial_as_at', 'date', 'Information correct as at'),
            ...moneyRow('entity_cash_reserves', 'Cash and readily accessible reserves'),
            ...moneyRow('property_holdings_value', 'Property holdings'),
            ...moneyRow('investments_outside_dowgate', 'Investments held outside Dowgate'),
            ...moneyRow('business_assets_value', 'Business or operating assets'),
            ...moneyRow('other_assets_value', 'Other material assets'),
            total('total_assets', 'Total Entity Assets', 'assets'),
            ...moneyRow('borrowings_outstanding', 'Borrowings and loans outstanding'),
            ...moneyRow('other_liabilities', 'Other liabilities'),
            ...moneyRow(
                'contingent_liabilities',
                'Contingent liabilities, guarantees and material commitments',
            ),
            total('total_liabilities', 'Total Entity Liabilities', 'liabilities'),
            ...moneyRow('trading_income', 'Trading or operating income'),
            ...moneyRow('investment_income', 'Investment income'),
            ...moneyRow('gifts_donations_grants', 'Gifts, donations or grants'),
            ...moneyRow('other_income', 'Other income'),
            total('total_income', 'Total Entity Income', 'income'),
            ...moneyRow('administrative_expenses', 'Operating and administrative expenditure'),
            ...moneyRow(
                'distributions',
                'Distributions, grants, beneficiary payments or pension payments',
            ),
            ...moneyRow('debt_servicing', 'Debt servicing'),
            ...moneyRow('other_commitments', 'Other regular commitments'),
            total('total_expenditure', 'Total Entity Expenditure', 'expenditure'),
            q(
                'financial_additional_info',
                'long_text',
                'Additional financial information or context',
            ),
        ],
    },
    {
        id: 'liquidity',
        title: 'Account Usage and Liquidity Requirements',
        description: 'How the entity uses its account, and its withdrawals and contributions.',
        intro: INTROS.liquidity,
        onboardingIntro: ONBOARDING_INTROS.liquidity,
        outro: 'Please tell us promptly if the expected pattern of contributions, withdrawals or payments changes materially.',
        tables: [
            { firstQuestionId: 'regular_withdrawals', heading: 'Expected Funding and Withdrawals' },
        ],
        kind: 'questions',
        questions: [
            q(
                'entity_account_usage',
                'multi_select',
                'How does the entity currently use, or expect to use, its Dowgate account(s)? Please select all that apply',
                {
                    options: [...ENTITY_ACCOUNT_USAGES],
                    onboardingLabel:
                        'How does the entity expect to use its Dowgate account(s)? Please select all that apply',
                },
            ),
            q(
                'entity_account_usage_other',
                'long_text',
                'If Other, please briefly explain the main purpose of the account and how the entity expects to use it',
                {
                    dependsOn: {
                        questionId: 'entity_account_usage',
                        equals: [ENTITY_ACCOUNT_USAGE_OTHER],
                    },
                },
            ),
            q(
                'regular_withdrawals',
                'yes_no',
                'Does the entity require regular withdrawals from its Dowgate portfolio?',
            ),
            q(
                'regular_withdrawals_portfolio',
                'short_text',
                'Regular withdrawals — portfolio or account',
                {
                    dependsOn: { questionId: 'regular_withdrawals', equals: YES },
                },
            ),
            q('regular_withdrawals_amount', 'currency', 'Regular withdrawals — amount', {
                dependsOn: { questionId: 'regular_withdrawals', equals: YES },
            }),
            q('regular_withdrawals_frequency', 'single_select', 'Regular withdrawals — frequency', {
                options: [...REGULAR_WITHDRAWAL_FREQUENCIES],
                control: 'dropdown',
                dependsOn: { questionId: 'regular_withdrawals', equals: YES },
            }),
            q('regular_withdrawals_frequency_other', 'short_text', 'Please specify the frequency', {
                dependsOn: { questionId: 'regular_withdrawals_frequency', equals: ['Other'] },
            }),
            q(
                'withdrawals_essential',
                'yes_no',
                'Will any withdrawals be used to meet essential operating expenditure, contractual obligations, beneficiary distributions, grants or pension payments?',
            ),
            q('withdrawals_essential_detail', 'long_text', 'If Yes, please provide details', {
                dependsOn: { questionId: 'withdrawals_essential', equals: YES },
            }),
            q(
                'capital_withdrawals',
                'yes_no',
                'Does the entity expect any one-off or occasional capital withdrawals?',
            ),
            q('capital_withdrawal_amount', 'currency', 'Capital withdrawals — amount', {
                dependsOn: { questionId: 'capital_withdrawals', equals: YES },
            }),
            q('capital_withdrawal_date', 'short_text', 'Capital withdrawals — expected date', {
                dependsOn: { questionId: 'capital_withdrawals', equals: YES },
            }),
            q('capital_withdrawal_purpose', 'short_text', 'Capital withdrawals — purpose', {
                dependsOn: { questionId: 'capital_withdrawals', equals: YES },
            }),
            q(
                'further_contributions',
                'yes_no',
                'Does the entity intend to make any further contributions?',
                {
                    onboardingLabel:
                        'Does the entity intend to make any further contributions after its initial investment?',
                },
            ),
            q('contribution_amount', 'currency', 'Further contributions — amount', {
                dependsOn: { questionId: 'further_contributions', equals: YES },
            }),
            q('contribution_frequency', 'short_text', 'Further contributions — frequency', {
                dependsOn: { questionId: 'further_contributions', equals: YES },
            }),
            q(
                'contribution_source_of_funds',
                'short_text',
                'Further contributions — source of funds',
                {
                    dependsOn: { questionId: 'further_contributions', equals: YES },
                },
            ),
            q(
                'third_party_payments',
                'yes_no',
                'Does the entity expect payments involving anyone other than its own verified bank account?',
            ),
            q('overseas_payments', 'yes_no', 'Does the entity expect any overseas payments?'),
            q(
                'payments_detail',
                'long_text',
                'If Yes to either question, provide the parties, countries, relationship to the entity and expected purpose',
            ),
        ],
    },
    {
        id: 'risk_assessment',
        title: 'Risk Tolerance and Capacity for Loss',
        description: 'The attitude of the entity to risk, and the loss it could withstand.',
        intro: INTROS.risk,
        onboardingIntro: ONBOARDING_INTROS.risk,
        kind: 'atr',
        questions: [],
    },
    {
        id: 'investment_restrictions',
        title: 'Investment Restrictions',
        description: 'The restrictions and the considerations that apply to the entity.',
        kind: 'questions',
        questions: [
            q(
                'restrictions_detail',
                'long_text',
                'Please provide details of any ethical, environmental, geographic, sector, stock-specific or other investment restrictions',
            ),
            q(
                'entity_considerations',
                'long_text',
                'Please provide details of any material legal, tax, accounting, charitable or regulatory considerations',
            ),
        ],
    },
    // The source of wealth and of the funds stays part of onboarding (Dowgate
    // decision of 2026-10-21). The onboarding form of an entity asks it before
    // the declaration, with the same words as the onboarding form of a person.
    {
        id: SOURCE_OF_WEALTH_SECTION_ID,
        title: SOURCE_OF_WEALTH_TITLE,
        description: SOURCE_OF_WEALTH_DESCRIPTION,
        intro: SOURCE_OF_WEALTH_INTRO,
        audience: 'onboarding',
        kind: 'questions',
        questions: SOURCE_OF_WEALTH_QUESTIONS.map((question) => ({
            ...question,
            options: question.options ? [...question.options] : undefined,
        })),
    },
    {
        id: 'declaration',
        title: 'Authority and Client Declaration',
        // SEP-318, decision 4. The client signs the declaration in the
        // signature pack. The approval of the fact find does not wait for it.
        description:
            'The client signs this declaration in the signature pack at Finalisation & Sign-off. It does not stop the approval of the fact find. The onboarding completes only when the signed declaration is on file.',
        intro: INTROS.declaration,
        tables: [
            {
                firstQuestionId: 'declaration_name',
                heading: 'Lead authorised person',
                intro: INTROS.declarationStatement,
                onboardingIntro: ONBOARDING_INTROS.declarationStatement,
            },
        ],
        kind: 'questions',
        questions: [
            forRefresh(
                q(
                    'authority_changed',
                    'yes_no',
                    "Since the previous review, has there been any change to the entity's authority to appoint or instruct Dowgate?",
                ),
            ),
            forRefresh(
                q(
                    'authority_changed_detail',
                    'long_text',
                    'If Yes, please provide details and supporting documentation, including a new authorised signatory list and confirmation of who will be the lead authorised person',
                    { dependsOn: { questionId: 'authority_changed', equals: YES } },
                ),
            ),
            q('declaration_name', 'short_text', 'Full name'),
            q('declaration_capacity', 'short_text', 'Role or capacity', {
                placeholder:
                    'For example director, trustee, partner, board member or authorised individual',
            }),
            q('declaration_date', 'date', 'Date'),
            q('declaration_contact', 'short_text', 'Email or telephone number'),
        ],
    },
];

/** Every question of the form, in its order. The risk questions are not in the list. */
export function entitySuitabilityQuestions(): EntitySuitabilityQuestion[] {
    return ENTITY_SUITABILITY_SECTIONS.flatMap((section) =>
        section.questions.map((question) => question),
    );
}
